/**
 * Canadian postal helpers for Places autocomplete / manual address entry.
 */

export function normalizeCanadianPostalCode(zip) {
    if (zip == null) {
        return '';
    }
    return String(zip).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function isCompleteCanadianPostalCode(zip) {
    const normalized = normalizeCanadianPostalCode(zip);
    return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(normalized);
}

export function isIncompletePostalCode(zip, countryCode = 'ca') {
    const country = String(countryCode || 'ca').toLowerCase();
    const raw = String(zip ?? '').trim();
    if (!raw) {
        return true;
    }
    if (country === 'us') {
        const digits = raw.replace(/\D/g, '');
        return digits.length < 5;
    }
    return !isCompleteCanadianPostalCode(raw);
}

export function pickBestPostalCode(...candidates) {
    const complete = candidates.find((c) => isCompleteCanadianPostalCode(c));
    if (complete) {
        return String(complete).trim();
    }
    const nonempty = candidates.find((c) => String(c ?? '').trim() !== '');
    return nonempty ? String(nonempty).trim() : '';
}

export function splitStreetNumberAndName(line) {
    const trimmed = String(line ?? '').trim();
    const match = trimmed.match(/^(\d+[A-Za-z]?)\s+(.+)$/);
    if (match) {
        return { streetNumber: match[1], street: match[2].trim() };
    }
    return { streetNumber: '', street: trimmed };
}

export function parseManualAddressFields(fullAddress, postalCode = '') {
    if (typeof fullAddress !== 'string' || fullAddress.trim() === '' || fullAddress.trim().toLowerCase() === 'false') {
        return {
            streetNumber: '',
            street: '',
            postalCode: String(postalCode ?? '').trim(),
            city: '',
        };
    }
    const parts = fullAddress
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
    const streetLine = parts[0] || '';
    const maybeCity = parts[1] && !/^(QC|Québec|Quebec|Canada|CA)$/i.test(parts[1])
        ? parts[1]
        : '';
    const { streetNumber, street } = splitStreetNumberAndName(streetLine);
    return {
        streetNumber,
        street,
        postalCode: String(postalCode ?? '').trim(),
        city: maybeCity,
    };
}

export function hasEnoughManualAddressToGeocode({ streetNumber, street, city, postalCode }) {
    const streetName = String(street ?? '').trim();
    if (!streetName) {
        return false;
    }
    return [streetNumber, city, postalCode].some((part) => String(part ?? '').trim() !== '');
}

export function formatManualAddressDescription({
    streetNumber,
    street,
    city,
    postalCode,
    countryCode = 'ca',
}) {
    const line = [String(streetNumber ?? '').trim(), String(street ?? '').trim()].filter(Boolean).join(' ');
    const cityPart = String(city ?? '').trim();
    const postalPart = String(postalCode ?? '').trim();
    const parts = [line, cityPart, postalPart].filter(Boolean);
    if (!parts.length) {
        return '';
    }
    const country = String(countryCode || 'ca').toUpperCase() === 'US' ? 'USA' : 'Canada';
    const region = country === 'USA' ? '' : 'QC';
    if (line || cityPart) {
        return [...parts, region, country].filter(Boolean).join(', ');
    }
    return parts.join(', ');
}

export function buildAddressSearchQuery({ streetNumber, street, city, postalCode, countryCode = 'ca' }) {
    if (!hasEnoughManualAddressToGeocode({ streetNumber, street, city, postalCode })) {
        return '';
    }
    const line = [streetNumber, street].filter(Boolean).join(' ').trim();
    const country = String(countryCode || 'ca').toUpperCase() === 'US' ? 'USA' : 'Canada';
    const region = country === 'USA' ? '' : 'QC';
    return [line, city, postalCode, region, country].filter(Boolean).join(', ');
}

export function detailsFromGeocodeResult(result) {
    const components = result?.address_components || [];
    const get = (type, short = false) => {
        const component = components.find((c) => c.types?.includes(type));
        if (!component) {
            return '';
        }
        return short ? component.short_name : component.long_name;
    };

    const streetNumber = get('street_number');
    const route = get('route');
    const city = get('locality')
        || get('postal_town')
        || get('sublocality_level_1')
        || get('sublocality')
        || get('administrative_area_level_3');
    const postalLong = get('postal_code');
    const postalShort = get('postal_code', true);
    const zip = pickBestPostalCode(postalLong, postalShort);
    const streetAddress = [streetNumber, route].filter(Boolean).join(' ').trim()
        || (result?.formatted_address || '').split(',')[0]?.trim()
        || '';

    return {
        streetNumber,
        route,
        streetAddress,
        city,
        zip,
        formattedAddress: result?.formatted_address || '',
    };
}
